/**
 * The org's plan: its prices (the server's list), and buying or managing it on the checkout site (`OrgOut.pay_url`), a
 * separate origin: RevenueCat Web Billing's SDK and Stripe run only there, never in the app. The org is RevenueCat's
 * customer (its app user id); what it bought reaches the server by webhook, and the server says what the plan is
 * (`OrgOut.plan`).
 */
import { api, queryString } from "../api/client.ts";
import type { Prices } from "../api/types.gen.ts";
import { t } from "../i18n/index.ts";
import { visitorCountry } from "./geo.ts";
import { shell } from "./native.ts";
import { CONTACT, formatAmount, type Period } from "./pricing.ts";

/** The plans on sale, smallest first, and the visitor's country they're priced for (indicative: the checkout charges
 *  the price of the buyer's country; a price unknown now is null). Without a country, the server goes by the
 *  language. */
export async function loadPrices(abort?: AbortSignal): Promise<Prices & { country: string | null }> {
  const country = await visitorCountry();
  const prices = await api.get<Prices>("/billing/prices", country ? { country } : undefined, abort);
  return { ...prices, country };
}

/**
 * A page of the checkout site: `checkout` (buying a tier) or `manage` (the subscription's own page). `back` is the
 * app's path to come back to.
 */
export function payUrl(
  base: string,
  page: "checkout" | "manage",
  q: {
    org: string;
    lang: string;
    back: string;
    tier?: string;
    period?: Period;
    currency?: string;
    country?: string | null;
  },
): string {
  const { back, ...rest } = q;
  return `${base}/${page}${queryString({ ...rest, return: back })}`;
}

/** `micros` (millionths) of `currency` as `lang`'s readers write it, with cents only when there are some (as the
 *  site's pricing does). */
export function formatPrice(micros: number, currency: string, lang: string): string {
  return formatAmount(Math.round(micros / 10_000) / 100, currency, lang);
}

/** A plan's name: its product's (Team, Business), the same in every language, or an invoiced one's. */
export function tierName(tier: string): string {
  if (tier === "invoiced") {
    return t("billing.invoiced");
  }
  return tier.startsWith("team") ? "Team" : tier.startsWith("business") ? "Business" : tier;
}

/** "6 of 15 people" (or just how many, without a limit). */
export function usageText(people: number, limit: number | null | undefined): string {
  return typeof limit === "number"
    ? t("billing.usage", { count: people, limit })
    : t("billing.people", { count: people });
}

/** Where to write about plans billed by invoice. */
export const SALES_EMAIL = CONTACT;

/**
 * Whether plans are sold here: not in the iOS app, where the App Store allows no purchase but its own, nor a way to
 * one (a link, words saying where): there, where the org stands only.
 */
export function sold(): boolean {
  return shell()?.platform !== "ios";
}
